import type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { OxsUserRole, type OxsUserRoles } from '@openx/types/user.ts';
import { type HTMLAttributes, type Key, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserRoleDescriptions, getUserRolelables } from './constants.ts';

export type UserRoleFieldProps = {
  disabled?: boolean;
  fullWidth?: boolean;
  isRootOrganization: boolean;
};

type RoleOptionProps = HTMLAttributes<HTMLLIElement> & { key: Key };

const INTERNAL_ROLES = Object.values(OxsUserRole).filter(role => role === OxsUserRole.ADMIN);
const EXTERNAL_ROLES = Object.values(OxsUserRole).filter(role => role !== OxsUserRole.ADMIN);

export const UserRoleField = ({ disabled, fullWidth, isRootOrganization }: UserRoleFieldProps) => {
  const { t } = useTranslation();
  const userRoleDescriptions = getUserRoleDescriptions(t);
  const userRoleLabels = getUserRolelables(t);
  const userRoleOptions = isRootOrganization ? INTERNAL_ROLES : EXTERNAL_ROLES;

  const getRoleOptionLabel = useCallback((option: OxsUserRoles) => userRoleLabels[option], [userRoleLabels]);

  const renderRoleOption = useCallback(
    ({ key, ...optionProps }: RoleOptionProps, option: OxsUserRoles, { selected }: AutocompleteRenderOptionState) => (
      <li data-test="autocomplete-option" key={key} {...optionProps}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Radio checked={selected} disableRipple size="small" sx={{ p: 0.25, pt: 0.75 }} tabIndex={-1} />
          <Stack>
            <Typography>{userRoleLabels[option]}</Typography>
            <Typography color="textSecondary" variant="body2">
              {userRoleDescriptions[option]}
            </Typography>
          </Stack>
        </Stack>
      </li>
    ),
    [userRoleDescriptions, userRoleLabels]
  );

  return (
    <RHFAutoCompleteVirtualize
      name={'role'}
      options={userRoleOptions}
      getOptionLabel={getRoleOptionLabel}
      textFieldProps={{
        label: t('Role'),
        placeholder: t('[ Select Role ]'),
      }}
      data-test="roles"
      disabled={disabled || isRootOrganization}
      fullWidth={fullWidth}
      itemSize={70}
      renderOption={renderRoleOption}
      slotProps={{
        listbox: {
          sx: {
            py: 0,
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: 'transparent !important',
            },
          },
        },
      }}
    />
  );
};
